/**
 * ============================================================================
 * ReviewCard - Compact Review Display Card
 * ============================================================================
 *
 * DESIGN PHILOSOPHY:
 * - Compact layout for scanning 100+ reviews quickly
 * - Clickable images that open fullscreen modal
 * - Inline reply form that slides in smoothly
 * - Mobile-responsive with proper touch targets
 *
 * ============================================================================
 */

import React, { useState } from 'react';
import {
  Star,
  CheckCircle,
  MessageSquare,
  Edit2,
  Trash2,
  Send,
  Image as ImageIcon,
} from 'lucide-react';
import { useConfirmation } from '../../contexts/ConfirmationContext';
import type { Review } from '../../types/reviews';
import { ImageModal } from './ImageModal';

interface ReviewCardProps {
  review: Review;
  onReply: (reviewId: number, replyText: string) => Promise<void>;
  onUpdateReply: (reviewId: number, replyText: string) => Promise<void>;
  onDeleteReply: (reviewId: number) => Promise<void>;
  isSubmitting: boolean;
  canReply?: boolean;
}

export function ReviewCard({
  review,
  onReply,
  onUpdateReply,
  onDeleteReply,
  isSubmitting,
  canReply = true,
}: ReviewCardProps) {
  const { confirm } = useConfirmation();
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const handleStartReply = () => {
    setShowReplyForm(true);
    setIsEditing(false);
    setReplyText('');
  };

  const handleStartEdit = () => {
    setShowReplyForm(true);
    setIsEditing(true);
    setReplyText(review.reply?.reply || '');
  };

  const handleCancel = () => {
    setShowReplyForm(false);
    setIsEditing(false);
    setReplyText('');
  };

  const handleSubmit = async () => {
    if (!replyText.trim() || replyText.length < 10) return;

    try {
      if (isEditing) {
        await onUpdateReply(review.id, replyText);
      } else {
        await onReply(review.id, replyText);
      }
      handleCancel();
    } catch (error) {
      // Error handling done in parent
    }
  };

  const handleDeleteReply = async () => {
    const confirmed = await confirm({
      title: 'Delete Reply',
      message: 'Are you sure you want to delete this reply?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;
    await onDeleteReply(review.id);
  };

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 p-4 hover:border-gray-300 transition-colors">
        {/* Header - Compact */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Avatar */}
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              {review.customer_avatar ? (
                <img
                  src={review.customer_avatar}
                  alt={review.customer_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <span className="text-sm font-bold text-white">
                  {review.customer_name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>

            {/* Customer Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-gray-900 text-sm truncate">
                  {review.customer_name}
                </h4>
                {review.is_verified && (
                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                )}
              </div>
              <div className="flex items-center gap-2">
                {renderStars(review.rating)}
                <span className="text-xs text-gray-500">{review.formatted_date}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Comment - Compact */}
        <p className="text-sm text-gray-700 leading-relaxed mb-3 line-clamp-3">
          {review.comment}
        </p>

        {/* Images - Compact Grid */}
        {review.images && review.images.length > 0 && (
          <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
            {review.images.map((image, index) => (
              <button
                key={index}
                onClick={() => handleImageClick(index)}
                className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 hover:opacity-90 transition-opacity group"
              >
                <img
                  src={image}
                  alt={`Review ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                  <ImageIcon className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Vendor Reply */}
        {review.reply && (
          <div className="ml-0 bg-blue-50 rounded-lg p-3 border border-blue-100 mb-3">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-semibold text-gray-900">
                  {review.reply.replied_by}
                </p>
                <p className="text-xs text-gray-500">{review.reply.formatted_date}</p>
              </div>
              {canReply && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleStartEdit}
                    className="p-1 hover:bg-blue-100 rounded text-blue-600"
                    aria-label="Edit reply"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleDeleteReply}
                    className="p-1 hover:bg-red-100 rounded text-red-600"
                    aria-label="Delete reply"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-700">{review.reply.reply}</p>
          </div>
        )}

        {/* Reply Form */}
        {showReplyForm ? (
          <div className="space-y-2">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write your reply..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500 resize-none"
              autoFocus
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {replyText.length > 0 && replyText.length < 10 && (
                  <span className="text-red-500">Reply must be at least 10 characters</span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !replyText.trim() || replyText.length < 10}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {isSubmitting ? (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  <span>{isEditing ? 'Update' : 'Send'}</span>
                </button>
              </div>
            </div>
          </div>
        ) : !review.reply && canReply && (
          <button
            onClick={handleStartReply}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>Reply to this review</span>
          </button>
        )}
      </div>

      {/* Image Modal */}
      {selectedImageIndex !== null && (
        <ImageModal
          images={review.images}
          currentIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
          onNavigate={setSelectedImageIndex}
        />
      )}
    </>
  );
}
